import { useState, useEffect, useMemo, useCallback, useRef, lazy, Suspense, memo } from 'react';
import { MapContainer, TileLayer, Marker, Tooltip, GeoJSON, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './MapComponent.css';
// AdminPanel.css also holds classes used by the main page
// (stats dashboard, user chip, header buttons, login screen).
// Import it here so those classes are present before the admin chunk loads.
import './AdminPanel.css';
import { api, setCsrf } from '../api';
import StatsDashboard from './StatsDashboard';

// AdminPanel + wizard aren't needed until an editor opens the panel.
// Splitting them off the main bundle shaves ~500KB off first paint (xlsx alone).
const AdminPanel = lazy(() => import(/* webpackChunkName: "admin" */ './AdminPanel'));
import {
  IconSearch, IconClose, IconSun, IconMoon, IconSettings, IconLogout, IconLock, IconShield,
  IconExternal, IconUser, IconInfo
} from './Icons';

const SESSION_KEY = 'defensafe_map_auth_v3';

// Mapbox raster basemap. CARTO's keyless tiles started arriving stamped
// "API KEY REQUIRED" (they watermark server-side and still return 200, so it
// fails silently), so the basemap moved here. `pk.` tokens are public by
// design and ship in the bundle -- this one is URL-restricted to
// map.defensafe.com, so it 403s from anywhere else (localhost included:
// `npm start` renders empty tiles unless localhost is added in the Mapbox
// dashboard alongside the production origin).
const MAPBOX_TOKEN = 'pk.eyJ1IjoibW9oYW1tZWRub3JpdCIsImEiOiJjbXRoYnh3ZHoxbWoxMnlxdzN6ZG1xeTBsIn0.i0Xci3vbTNeAQuEdWSIQdg';
const MAPBOX_ATTR = '&copy; <a href="https://www.mapbox.com/about/maps/" target="_blank" rel="noopener">Mapbox</a> &copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>';

// Pre-build the 8 possible marker icons ONCE (was: new L.Icon per marker per render).
// Combinations: dark|light × active|inactive × normal|highlighted.
function makeIcon(isDark, isInactive, isHighlighted) {
  return new L.Icon({
    iconUrl: isDark ? '/pin-light.png' : '/pin-dark.png',
    iconSize: [25, 35],
    iconAnchor: [12, 35],
    tooltipAnchor: [1, -35],
    className: `custom-pin-icon${isHighlighted ? ' highlighted' : ''}${isInactive ? ' inactive' : ''}`,
  });
}
const ICON_CACHE = {
  dark:  { normal: makeIcon(true,  false, false), inactive: makeIcon(true,  true, false), highlighted: makeIcon(true,  false, true) },
  light: { normal: makeIcon(false, false, false), inactive: makeIcon(false, true, false), highlighted: makeIcon(false, false, true) },
};
function iconFor(isDark, isInactive, isHighlighted) {
  const t = isDark ? ICON_CACHE.dark : ICON_CACHE.light;
  if (isHighlighted) return t.highlighted;
  if (isInactive) return t.inactive;
  return t.normal;
}

function FitBounds({ locations }) {
  const map = useMap();
  useEffect(() => {
    if (locations.length > 0) {
      const bounds = L.latLngBounds(locations.map(l => [l.lat, l.lng]));
      map.fitBounds(bounds, { padding: [80, 80] });
    }
  }, [locations, map]);
  return null;
}

// Cache Syria border GeoJSON for a week — avoids the OSM roundtrip on every load.
const BORDER_CACHE_KEY = 'defensafe_syria_border_v1';
const BORDER_CACHE_TTL_MS = 7 * 24 * 3600 * 1000;

async function fetchSyriaBorder() {
  try {
    const raw = localStorage.getItem(BORDER_CACHE_KEY);
    if (raw) {
      const c = JSON.parse(raw);
      if (c.ts && Date.now() - c.ts < BORDER_CACHE_TTL_MS && c.data) return c.data;
    }
  } catch (_) {}
  const sources = [
    'https://nominatim.openstreetmap.org/details.php?osmtype=R&osmid=184840&polygon_geojson=1&format=json',
    'https://raw.githubusercontent.com/georgique/world-geojson/develop/countries/syria.json',
    'https://raw.githubusercontent.com/glynnbird/countriesgeojson/master/syria.geojson',
  ];
  for (const url of sources) {
    try {
      const r = await fetch(url);
      if (!r.ok) continue;
      const d = await r.json();
      const feature = d?.geometry
        ? { type: 'Feature', properties: { name: 'Syria' }, geometry: d.geometry }
        : d;
      if (!feature) continue;
      try { localStorage.setItem(BORDER_CACHE_KEY, JSON.stringify({ ts: Date.now(), data: feature })); } catch (_) {}
      return feature;
    } catch (_) {}
  }
  return null;
}

function SyrianBorder() {
  const [geoData, setGeoData] = useState(null);
  useEffect(() => { fetchSyriaBorder().then(setGeoData); }, []);
  if (!geoData) return null;
  // Static style — the "breathing" pulse is a CSS animation on .syrian-border-path,
  // so React re-renders are not needed.
  return (
    <GeoJSON
      data={geoData}
      style={() => ({
        className: 'syrian-border-path',
        color: '#F5333F',
        weight: 2,
        opacity: 0.85,
        fillColor: 'transparent',
        fillOpacity: 0,
        lineCap: 'round',
        lineJoin: 'round',
      })}
    />
  );
}

function FlyToLocation({ location }) {
  const map = useMap();
  useEffect(() => { if (location) map.flyTo([location.lat, location.lng], 14, { duration: 1 }); }, [location, map]);
  return null;
}

const CustomMarker = memo(function CustomMarker({ location, isDark, onSelect, isHighlighted }) {
  const isInactive = (location.status || 'active') !== 'active';
  const icon = iconFor(isDark, isInactive, isHighlighted);
  const handleClick = useCallback(() => onSelect(location), [location, onSelect]);
  return (
    <Marker position={[location.lat, location.lng]} icon={icon} eventHandlers={{ click: handleClick }}>
      <Tooltip direction="top" offset={[0, -10]} opacity={1} className={`custom-tooltip ${isDark ? 'dark' : 'light'} ${isHighlighted ? 'highlighted' : ''}`}>
        <span>{location.name}{isInactive ? ' (موقوف)' : ''}</span>
      </Tooltip>
    </Marker>
  );
});

function LocationPanel({ location, governorates, onClose, isDark }) {
  if (!location) return null;
  const gov = governorates.find(g => g.key === location.governorate);
  return (
    <div className={`location-panel ${isDark ? 'dark' : 'light'}`}>
      <button className="icon-btn close-btn" onClick={onClose} aria-label="إغلاق"><IconClose size={16}/></button>
      <div className="panel-content">
        <h2>{location.name}</h2>
        {gov && <div className="info-row"><span className="label">المحافظة</span><span className="value">{gov.name_ar}</span></div>}
        <div className="info-row"><span className="label">الإحداثيات</span><span className="value mono">{location.lat.toFixed(6)}, {location.lng.toFixed(6)}</span></div>
        {location.status && location.status !== 'active' && <div className="info-row"><span className="label">الحالة</span><span className="value">{location.status === 'inactive' ? 'موقوف' : 'مغلق'}</span></div>}
        {location.url && (
          <a href={location.url} target="_blank" rel="noopener noreferrer" className="map-link">
            <IconExternal size={15}/><span>فتح في خرائط جوجل</span>
          </a>
        )}
      </div>
    </div>
  );
}

function SearchBox({ locations, onSelect, isDark }) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  // 150ms debounce so filter doesn't run on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 150);
    return () => clearTimeout(t);
  }, [query]);

  const results = useMemo(() => {
    if (debounced.length < 2) return [];
    const q = debounced.toLowerCase();
    const out = [];
    for (let i = 0; i < locations.length && out.length < 6; i++) {
      if (locations[i].name.toLowerCase().includes(q)) out.push(locations[i]);
    }
    return out;
  }, [debounced, locations]);
  useEffect(() => { setIsOpen(results.length > 0); }, [results]);
  return (
    <div className={`search-box ${isDark ? 'dark' : 'light'}`}>
      <div className="search-input-wrap">
        <IconSearch size={16} className="search-icon" />
        <input type="text" placeholder="ابحث عن موقع..." value={query} onChange={e => setQuery(e.target.value)} aria-label="بحث المواقع" />
        {query && <button className="icon-btn clear-btn" onClick={() => setQuery('')} aria-label="مسح البحث"><IconClose size={12}/></button>}
      </div>
      {isOpen && (
        <ul className="search-results" role="listbox">
          {results.map(l => (
            <li key={l.id} role="option" onClick={() => { onSelect(l); setQuery(''); setIsOpen(false); }}>
              <img src={isDark ? '/pin-light.png' : '/pin-dark.png'} alt="" className="result-pin" />
              <span className="result-name">{l.name}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const ROLE_BADGE = { viewer: 'مشاهد', editor: 'محرر', admin: 'مدير' };

function MapView() {
  const [locations, setLocations] = useState([]);
  const [governorates, setGovernorates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDark, setIsDark] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [highlightedId, setHighlightedId] = useState(null);
  const [role, setRole] = useState(null);
  const [username, setUsername] = useState('');
  const [userId, setUserId] = useState(null);
  const [actor, setActor] = useState('');
  const [adminOpen, setAdminOpen] = useState(false);
  const [govFilter, setGovFilter] = useState(null);

  const canEdit = role === 'editor' || role === 'admin';

  const reload = useCallback(async () => {
    const data = await api.listLocations();
    setLocations(data.locations || []);
    setGovernorates(data.governorates || []);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const me = await api.me();
        setRole(me.role); setUsername(me.username || ''); setUserId(me.user_id || null);
        if (me.csrf) setCsrf(me.csrf);
        await reload();
      } catch (_) {} finally { setLoading(false); }
    })();
  }, [reload]);

  const filteredLocations = useMemo(
    () => govFilter ? locations.filter(l => l.governorate === govFilter) : locations,
    [locations, govFilter]
  );
  const visibleLocations = useMemo(
    () => canEdit ? filteredLocations : filteredLocations.filter(l => (l.status || 'active') === 'active'),
    [filteredLocations, canEdit]
  );

  const defaultCenter = useMemo(() => {
    if (locations.length === 0) return [33.5, 36.3];
    const a = locations.reduce((s, l) => [s[0] + l.lat, s[1] + l.lng], [0, 0]);
    return [a[0] / locations.length, a[1] / locations.length];
  }, [locations]);

  // 512px tiles, so the TileLayer below needs tileSize/zoomOffset to match.
  const tileUrl = `https://api.mapbox.com/styles/v1/mapbox/${isDark ? 'dark-v11' : 'light-v11'}/tiles/512/{z}/{x}/{y}{r}?access_token=${MAPBOX_TOKEN}`;

  if (loading) {
    return <div className={`loading-screen ${isDark ? 'dark' : 'light'}`}><div className="loader" /><p>جاري تحميل المواقع...</p></div>;
  }
  if (!role) return <PasswordScreen onSuccess={async (r, u, id, a) => { setRole(r); setUsername(u); setUserId(id); setActor(a); await reload(); }} />;

  return (
    <div className={`app-container ${isDark ? 'dark' : 'light'}`}>
      <header className="app-header">
        <div className="header-inner">
          <div className="logo-area">
            <img src={isDark ? '/logo-light.png' : '/logo-dark.png'} alt="Defensafe" className="logo-img" />
          </div>
          <div className="header-title">
            <h1>خريطة المواقع الأمنية</h1>
            <p>Defensafe Location Intelligence</p>
          </div>
          <div className="header-actions">
            <div className="stats-pill" aria-label="عدد المواقع">
              <IconShield size={14} className="pill-icon" />
              <span className="num">{visibleLocations.length}</span>
              <span className="txt">موقع</span>
            </div>
            {canEdit && (
              <button className={`header-btn ${adminOpen ? 'is-active' : ''}`} onClick={() => setAdminOpen(o => !o)} aria-pressed={adminOpen}>
                <IconSettings size={15}/><span>لوحة الإدارة</span>
              </button>
            )}
            {role && (
              <div className="user-chip" title={username}>
                <IconUser size={13}/>
                <span className="user-name">{username || actor || 'user'}</span>
                <span className={`role-badge role-${role}`}>{ROLE_BADGE[role] || role}</span>
              </div>
            )}
            <button className="icon-btn header-icon-btn" onClick={() => setIsDark(d => !d)} aria-label={isDark ? 'الوضع النهاري' : 'الوضع الليلي'} title={isDark ? 'الوضع النهاري' : 'الوضع الليلي'}>
              {isDark ? <IconSun size={16}/> : <IconMoon size={16}/>}
            </button>
            {role && (
              <button className="icon-btn header-icon-btn" onClick={async () => { try { await api.logout(); } finally { setRole(null); setUsername(''); setUserId(null); setCsrf(null); } }} aria-label="تسجيل الخروج" title="تسجيل الخروج">
                <IconLogout size={16}/>
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="map-area">
        <StatsDashboard
          locations={canEdit ? locations : locations.filter(l => (l.status || 'active') === 'active')}
          governorates={governorates}
          activeGov={govFilter}
          onSelectGov={setGovFilter}
        />
        <MapContainer center={defaultCenter} zoom={7} className="leaflet-map" scrollWheelZoom>
          <TileLayer attribution={MAPBOX_ATTR} url={tileUrl} tileSize={512} zoomOffset={-1} />
          <SyrianBorder />
          <FitBounds locations={visibleLocations} />
          <FlyToLocation location={selectedLocation} />
          <MarkerClusterGroup
            key={`cluster-${isDark ? 'dark' : 'light'}-${visibleLocations.length}`}
            chunkedLoading maxClusterRadius={35} spiderfyOnMaxZoom showCoverageOnHover={false}
            iconCreateFunction={(cluster) => {
              const count = cluster.getChildCount();
              const pinIcon = isDark ? '/pin-light.png' : '/pin-dark.png';
              return L.divIcon({
                html: `<div class="cluster-marker ${isDark ? 'dark' : 'light'}"><img src="${pinIcon}" alt="" class="cluster-pin-icon" /><span class="cluster-count">${count}</span></div>`,
                className: 'custom-cluster-icon', iconSize: L.point(40, 50, true), iconAnchor: L.point(20, 50),
              });
            }}
          >
            {visibleLocations.map((loc) => (
              <CustomMarker key={loc.id} location={loc} isDark={isDark}
                onSelect={setSelectedLocation} isHighlighted={loc.id === highlightedId} />
            ))}
          </MarkerClusterGroup>
        </MapContainer>

        <LocationPanel location={selectedLocation} governorates={governorates} onClose={() => setSelectedLocation(null)} isDark={isDark} />

        {canEdit && adminOpen && (
          <Suspense fallback={null}>
            <AdminPanel
              isOpen={adminOpen}
              onClose={() => setAdminOpen(false)}
              locations={locations}
              governorates={governorates}
              onChanged={reload}
              onShowOnMap={(loc) => loc?.id && setSelectedLocation(loc)}
              role={role}
              selfId={userId}
            />
          </Suspense>
        )}
      </main>

      <footer className="app-footer">
        <div className="footer-inner">
          <div className="footer-left">
            <SearchBox locations={visibleLocations} onSelect={(l) => { setSelectedLocation(l); setHighlightedId(l.id); setTimeout(() => setHighlightedId(null), 5000); }} isDark={isDark} />
          </div>
          <div className="footer-center">
            <span className="hint"><IconInfo size={12}/> انقر على الموقع للتفاصيل · انقر على المحافظة للتصفية</span>
          </div>
          <div className="footer-right">© {new Date().getFullYear()} Defensafe</div>
        </div>
      </footer>
    </div>
  );
}

function PasswordScreen({ onSuccess }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const r = await api.login({ password });
      if (r.csrf) setCsrf(r.csrf);
      sessionStorage.setItem(SESSION_KEY, JSON.stringify({ role: r.role, username: r.username, actor: r.actor }));
      onSuccess(r.role, r.username, r.user_id, r.actor);
    } catch (e) {
      setError(e.message || 'فشل تسجيل الدخول');
      setPassword('');
    } finally { setLoading(false); }
  };
  return (
    <div className="password-screen">
      <div className="password-card">
        <div className="password-logo"><img src="/logo-light.png" alt="Defensafe" /></div>
        <h1>خريطة المواقع</h1>
        <p>أدخل كلمة المرور للوصول.</p>
        <form onSubmit={submit}>
          <label className="login-field">
            <IconLock size={15}/>
            <input type="password" placeholder="كلمة المرور" value={password} onChange={e => setPassword(e.target.value)} autoFocus disabled={loading} dir="ltr" autoComplete="current-password" name="defensafe-password"/>
          </label>
          {error && <div className="password-error">{error}</div>}
          <button type="submit" className="btn btn-primary login-submit" disabled={loading || !password}>
            {loading ? '...جاري التحقق' : 'دخول'}
          </button>
        </form>
        <div className="password-footer">© {new Date().getFullYear()} Defensafe. All rights reserved.</div>
      </div>
    </div>
  );
}

export default MapView;
